import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export default function NewProject() {
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'manual' | 'image'
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState('');
  const [planningLevel, setPlanningLevel] = useState('moderate'); // automatic, moderate, detailed
  // Image analysis state
  const [dragOver, setDragOver] = useState(false);
  const [imagePreview, setImagePreview] = useState(null); // data URL for display
  const [imageBase64, setImageBase64] = useState(null);   // pure base64 for API
  const [imageUrl, setImageUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const fileInputRef = useRef(null);
  const [criteria, setCriteria] = useState({
    style: '',
    artStyle: '',
    subjects: '',
    colors: '',
    borders: '',
    mood: '',
    audience: '',
    useCase: '',
    size: '',
    quantity: ''
  }); // Design criteria
  const [criteriaLocked, setCriteriaLocked] = useState({
    style: false,
    artStyle: false,
    subjects: false,
    colors: false,
    borders: false,
    mood: false,
    audience: false,
    useCase: false,
    size: false,
    quantity: false
  }); // Track which fields are manually edited
  const [generating, setGenerating] = useState(false); // Image generation in progress
  const [generationProgress, setGenerationProgress] = useState(0); // 0-100
  const [showHelp, setShowHelp] = useState(false); // Help tooltip visibility
  const navigate = useNavigate();

  // System prompts based on planning level
  const getSystemPrompt = () => {
    // Build current criteria summary
    const filledCriteria = Object.entries(criteria)
      .filter(([_, value]) => value && value.trim())
      .map(([key, value]) => `  - ${key}: ${value}`)
      .join('\n');

    const criteriaSummary = filledCriteria
      ? `\n\nCurrent design criteria already defined:\n${filledCriteria}\n\nDon't ask about criteria that are already filled. Focus on empty criteria.`
      : '';

    switch (planningLevel) {
      case 'automatic':
        return `You are a sticker design assistant. The user wants a quick, automatic design with minimal questions. Take their single request and immediately provide a complete sticker theme with all design decisions made automatically. Be creative and confident. Respond with: style, art style, colors, subjects, borders, mood, audience, use case, and any other design criteria. Keep it brief and decisive.${criteriaSummary}`;
      case 'moderate':
        return `You are a helpful sticker design assistant. Ask a few clarifying questions (2-3) to refine the design, then provide a complete theme. Balance efficiency with customization. Be creative, enthusiastic, and concise. Focus on: style, art style, subjects, colors, borders, mood, target audience, and use cases.${criteriaSummary}`;
      case 'detailed':
        return `You are a thorough sticker design assistant. Ask detailed questions to fully understand the user's vision. Explore: visual style, art style (realistic/cartoon/vintage), subjects, color palette, borders/layout, mood/vibe, target audience, use cases, size preferences, and quantity. Take your time to craft the perfect theme. Be creative and comprehensive.${criteriaSummary}`;
      default:
        return `You are a helpful sticker design assistant. Help users create amazing sticker themes. Be creative, enthusiastic, and ask clarifying questions to refine their ideas. Keep responses concise and friendly.${criteriaSummary}`;
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading || generating) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: getSystemPrompt()
            },
            ...messages,
            userMessage
          ]
        })
      });

      const data = await response.json();

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.message
      }]);

      // Extract criteria after assistant response
      extractCriteria([...messages, userMessage, { role: 'assistant', content: data.message }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I had trouble connecting. Please try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Extract design criteria from conversation
  const extractCriteria = (conversation) => {
    // Simple extraction - looks for keywords in messages
    const allText = conversation.map(m => m.content).join(' ').toLowerCase();

    const extracted = {
      style: extractKeyword(allText, ['cute', 'minimalist', 'bold', 'simple', 'complex', 'detailed', 'clean']),
      artStyle: extractKeyword(allText, ['cartoon', 'realistic', 'abstract', 'vintage', 'retro', 'modern', 'hand-drawn', 'digital', 'watercolor', 'pixel art']),
      subjects: extractKeyword(allText, ['cat', 'dog', 'dinosaur', 'animal', 'flower', 'food', 'space', 'nature', 'tech', 'emoji', 'character', 'beach', 'shapes', 'geometric']),
      colors: extractKeyword(allText, ['blue', 'red', 'green', 'yellow', 'purple', 'pink', 'orange', 'black', 'white', 'pastel', 'vibrant', 'colorful', 'monochrome', 'rainbow', 'neon']),
      borders: extractKeyword(allText, ['border', 'outline', 'no border', 'white border', 'black border', 'rounded', 'square']),
      mood: extractKeyword(allText, ['happy', 'fun', 'serious', 'playful', 'calm', 'energetic', 'whimsical', 'professional', 'silly', 'adventurous']),
      audience: extractKeyword(allText, ['kids', 'adults', 'teens', 'toddlers', 'professionals', 'students', 'everyone', 'children']),
      useCase: extractKeyword(allText, ['laptop', 'water bottle', 'notebook', 'phone', 'car', 'skateboard', 'guitar', 'planner']),
      size: extractKeyword(allText, ['small', 'medium', 'large', 'tiny', 'big', 'inch', 'cm']),
      quantity: extractKeyword(allText, ['12', 'dozen', 'set', 'pack', 'few', 'many'])
    };

    // Only update fields that aren't locked
    setCriteria(prev => ({
      style: criteriaLocked.style ? prev.style : (extracted.style || prev.style),
      artStyle: criteriaLocked.artStyle ? prev.artStyle : (extracted.artStyle || prev.artStyle),
      subjects: criteriaLocked.subjects ? prev.subjects : (extracted.subjects || prev.subjects),
      colors: criteriaLocked.colors ? prev.colors : (extracted.colors || prev.colors),
      borders: criteriaLocked.borders ? prev.borders : (extracted.borders || prev.borders),
      mood: criteriaLocked.mood ? prev.mood : (extracted.mood || prev.mood),
      audience: criteriaLocked.audience ? prev.audience : (extracted.audience || prev.audience),
      useCase: criteriaLocked.useCase ? prev.useCase : (extracted.useCase || prev.useCase),
      size: criteriaLocked.size ? prev.size : (extracted.size || prev.size),
      quantity: criteriaLocked.quantity ? prev.quantity : (extracted.quantity || prev.quantity)
    }));
  };

  const extractKeyword = (text, keywords) => {
    const found = keywords.filter(kw => text.includes(kw));
    return found.length > 0 ? found.join(', ') : '';
  };

  // Handle manual field edit - locks the field
  const handleCriteriaChange = (field, value) => {
    setCriteria(prev => ({ ...prev, [field]: value }));
    setCriteriaLocked(prev => ({ ...prev, [field]: true }));
  };

  // Toggle lock state
  const toggleLock = (field) => {
    setCriteriaLocked(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // ── Image Analysis ──────────────────────────────────────────────────────────

  const loadImageFile = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please provide an image file (JPG, PNG, GIF, WebP, etc.)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setImagePreview(dataUrl);
      // Strip the "data:image/...;base64," prefix to get pure base64
      setImageBase64(dataUrl.split(',')[1]);
      setImageUrl('');
    };
    reader.readAsDataURL(file);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    loadImageFile(file);
  };

  const handleFileInput = (e) => {
    loadImageFile(e.target.files[0]);
  };

  const handlePaste = useCallback((e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    if (imageItem) {
      e.preventDefault();
      loadImageFile(imageItem.getAsFile());
    }
  }, []);

  const handleAnalyzeImage = async () => {
    if (!imageBase64 && !imageUrl.trim()) {
      alert('Please upload an image or enter a URL first.');
      return;
    }

    setAnalyzing(true);
    try {
      const payload = imageBase64
        ? { imageBase64 }
        : { imageUrl: imageUrl.trim() };

      const response = await fetch('/api/projects/analyze-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.error) throw new Error(data.error);

      // Fill in theme if empty
      if (!theme.trim() && data.suggestedTheme) {
        setTheme(data.suggestedTheme);
      }

      // Fill all criteria fields (unlock them first)
      setCriteria(prev => ({
        style:     data.criteria.style     || prev.style,
        artStyle:  data.criteria.artStyle  || prev.artStyle,
        subjects:  data.criteria.subjects  || prev.subjects,
        colors:    data.criteria.colors    || prev.colors,
        borders:   data.criteria.borders   || prev.borders,
        mood:      data.criteria.mood      || prev.mood,
        audience:  data.criteria.audience  || prev.audience,
        useCase:   data.criteria.useCase   || prev.useCase,
        size:      data.criteria.size      || prev.size,
        quantity:  data.criteria.quantity  || prev.quantity
      }));

      // Reset all locks so user can see fresh AI values
      setCriteriaLocked({
        style: false, artStyle: false, subjects: false, colors: false,
        borders: false, mood: false, audience: false, useCase: false,
        size: false, quantity: false
      });

      console.log(`[Vision] Criteria filled from image. Theme: "${data.suggestedTheme}"`);
    } catch (err) {
      console.error('Image analysis error:', err);
      alert('Failed to analyze image: ' + err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const clearImage = () => {
    setImagePreview(null);
    setImageBase64(null);
    setImageUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Calculate progress ──────────────────────────────────────────────────────
  const criteriaCount = Object.values(criteria).filter(v => v && v.trim()).length;
  const totalCriteria = Object.keys(criteria).length;
  const canStartGeneration = criteriaCount >= 3 && theme.trim(); // Need at least 3 criteria + project name

  const createProject = async () => {
    if (!theme.trim()) {
      alert('Please enter a project name/theme first!');
      return;
    }

    setGenerating(true);
    setGenerationProgress(0);

    try {
      // Create project in database
      setGenerationProgress(30);
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          theme,
          conversation: messages,
          criteria: criteria
        })
      });

      const project = await response.json();
      setGenerationProgress(100);

      // Small delay for UX
      await new Promise(resolve => setTimeout(resolve, 400));

      // Navigate to project editor where generation will start
      navigate(`/projects/${project.id}`);
    } catch (error) {
      console.error('Create project error:', error);
      alert('Failed to create project. Please try again.');
      setGenerating(false);
      setGenerationProgress(0);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">New Sticker Project</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Left Column */}
        <div className="lg:col-span-2 space-y-4">

          {/* Tab Selector */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {[
              { id: 'chat',   label: '💬 Chat with AI' },
              { id: 'manual', label: '✏️ Manual' },
              { id: 'image',  label: '🖼️ From Image' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Chat Tab ── */}
          {activeTab === 'chat' && (
          <div className="card">
            {/* Messages */}
            <div className="h-96 overflow-y-auto mb-4 space-y-4 bg-gray-50 p-4 rounded-lg">
              {messages.length === 0 && (
                <div className="text-center text-gray-500 mt-20">
                  <p className="text-lg mb-2">👋 Hi! I'm your sticker design assistant!</p>
                  <p className="text-sm">Tell me about the stickers you want to create.</p>
                  <p className="text-xs mt-4 text-gray-400">
                    Example: "I want cute cat stickers for my laptop"
                  </p>
                </div>
              )}
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-900 border border-gray-200'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white text-gray-900 border border-gray-200 px-4 py-2 rounded-lg">
                    <p className="text-sm">Thinking...</p>
                  </div>
                </div>
              )}
            </div>
            <form onSubmit={sendMessage} className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={generating ? "Please wait - generating images..." : "Type your message..."}
                className="input flex-1"
                disabled={loading || generating}
              />
              <button type="submit" disabled={loading || generating || !input.trim()} className="btn-primary px-6">
                Send
              </button>
            </form>
            {generating && (
              <p className="text-xs text-amber-600 mt-2 text-center">
                ⏳ Image generation in progress. Chat will resume when complete.
              </p>
            )}
          </div>
          )}

          {/* ── Manual Tab ── */}
          {activeTab === 'manual' && (
          <div className="card">
            <p className="text-sm text-gray-600 mb-4">
              Fill in the design criteria directly. Any field you complete will be used to guide sticker generation.
            </p>
            <p className="text-xs text-indigo-600 bg-indigo-50 rounded p-2">
              💡 Tip: Fill in the <strong>Design Criteria</strong> panel below — your changes appear here in real time.
            </p>
          </div>
          )}

          {/* ── From Image Tab ── */}
          {activeTab === 'image' && (
          <div className="card" onPaste={handlePaste}>
            <p className="text-sm text-gray-600 mb-4">
              Upload an image and Gemma AI will analyze it and fill in all your sticker design criteria automatically. ✨
            </p>

            {/* Drop Zone */}
            {!imagePreview ? (
              <div
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-all cursor-pointer ${
                  dragOver
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="text-5xl mb-3">🖼️</div>
                <p className="text-gray-700 font-medium mb-1">Drop an image here</p>
                <p className="text-gray-500 text-sm mb-4">or click to browse · or Ctrl+V to paste</p>
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                >
                  Browse Files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileInput}
                />
              </div>
            ) : (
              <div className="relative mb-4">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full max-h-64 object-contain rounded-lg border border-gray-200 bg-gray-50"
                />
                <button
                  onClick={clearImage}
                  className="absolute top-2 right-2 bg-white border border-gray-300 rounded-full w-7 h-7 flex items-center justify-center text-gray-600 hover:bg-red-50 hover:text-red-600 text-sm shadow"
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
            )}

            {/* URL Input */}
            <div className="mt-4">
              <p className="text-xs text-gray-500 mb-2 text-center">— or paste an image URL —</p>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => { setImageUrl(e.target.value); setImagePreview(null); setImageBase64(null); }}
                  placeholder="https://example.com/image.jpg"
                  className="input flex-1 text-sm"
                />
              </div>
            </div>

            {/* Analyze Button */}
            <button
              onClick={handleAnalyzeImage}
              disabled={analyzing || (!imageBase64 && !imageUrl.trim())}
              className={`w-full mt-4 py-3 px-4 rounded-lg font-semibold text-white transition-all ${
                !analyzing && (imageBase64 || imageUrl.trim())
                  ? 'bg-indigo-600 hover:bg-indigo-700 shadow-md'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {analyzing ? '🔍 Analyzing with Gemma AI...' : '🔍 Analyze Image & Fill Criteria'}
            </button>

            {analyzing && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                This may take 10-30 seconds while Gemma examines the image...
              </p>
            )}
          </div>
          )}

          {/* Criteria Tree - Always visible */}
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">
                📋 Design Criteria
              </h3>
              <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                {criteriaCount}/{totalCriteria} complete
              </span>
            </div>

            <div className="space-y-3">
              {/* Style */}
              <div>
                <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                  <span>Visual Style {criteria.style && '✓'}</span>
                  <button
                    onClick={() => toggleLock('style')}
                    className="text-xs"
                    title={criteriaLocked.style ? 'Unlock (allow AI to update)' : 'Lock (protect from AI updates)'}
                  >
                    {criteriaLocked.style ? '🔒' : '🔓'}
                  </button>
                </label>
                <input
                  type="text"
                  value={criteria.style}
                  onChange={(e) => handleCriteriaChange('style', e.target.value)}
                  placeholder="e.g., cute, minimalist, bold"
                  className={`input text-sm ${criteria.style ? 'bg-green-50 border-green-200' : ''}`}
                />
              </div>

              {/* Art Style */}
              <div>
                <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                  <span>Art Style {criteria.artStyle && '✓'}</span>
                  <button
                    onClick={() => toggleLock('artStyle')}
                    className="text-xs"
                    title={criteriaLocked.artStyle ? 'Unlock' : 'Lock'}
                  >
                    {criteriaLocked.artStyle ? '🔒' : '🔓'}
                  </button>
                </label>
                <input
                  type="text"
                  value={criteria.artStyle}
                  onChange={(e) => handleCriteriaChange('artStyle', e.target.value)}
                  placeholder="e.g., cartoon, realistic, vintage"
                  className={`input text-sm ${criteria.artStyle ? 'bg-green-50 border-green-200' : ''}`}
                />
              </div>

              {/* Subjects */}
              <div>
                <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                  <span>Subjects/Topic {criteria.subjects && '✓'}</span>
                  <button
                    onClick={() => toggleLock('subjects')}
                    className="text-xs"
                    title={criteriaLocked.subjects ? 'Unlock' : 'Lock'}
                  >
                    {criteriaLocked.subjects ? '🔒' : '🔓'}
                  </button>
                </label>
                <input
                  type="text"
                  value={criteria.subjects}
                  onChange={(e) => handleCriteriaChange('subjects', e.target.value)}
                  placeholder="e.g., dinosaur, cat, flowers"
                  className={`input text-sm ${criteria.subjects ? 'bg-green-50 border-green-200' : ''}`}
                />
              </div>

              {/* Colors */}
              <div>
                <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                  <span>Colors {criteria.colors && '✓'}</span>
                  <button
                    onClick={() => toggleLock('colors')}
                    className="text-xs"
                    title={criteriaLocked.colors ? 'Unlock' : 'Lock'}
                  >
                    {criteriaLocked.colors ? '🔒' : '🔓'}
                  </button>
                </label>
                <input
                  type="text"
                  value={criteria.colors}
                  onChange={(e) => handleCriteriaChange('colors', e.target.value)}
                  placeholder="e.g., blue, pink, pastel"
                  className={`input text-sm ${criteria.colors ? 'bg-green-50 border-green-200' : ''}`}
                />
              </div>

              {/* Borders */}
              <div>
                <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                  <span>Borders/Layout {criteria.borders && '✓'}</span>
                  <button
                    onClick={() => toggleLock('borders')}
                    className="text-xs"
                    title={criteriaLocked.borders ? 'Unlock' : 'Lock'}
                  >
                    {criteriaLocked.borders ? '🔒' : '🔓'}
                  </button>
                </label>
                <input
                  type="text"
                  value={criteria.borders}
                  onChange={(e) => handleCriteriaChange('borders', e.target.value)}
                  placeholder="e.g., white border, no border"
                  className={`input text-sm ${criteria.borders ? 'bg-green-50 border-green-200' : ''}`}
                />
              </div>

              {/* Mood */}
              <div>
                <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                  <span>Mood/Vibe {criteria.mood && '✓'}</span>
                  <button
                    onClick={() => toggleLock('mood')}
                    className="text-xs"
                    title={criteriaLocked.mood ? 'Unlock' : 'Lock'}
                  >
                    {criteriaLocked.mood ? '🔒' : '🔓'}
                  </button>
                </label>
                <input
                  type="text"
                  value={criteria.mood}
                  onChange={(e) => handleCriteriaChange('mood', e.target.value)}
                  placeholder="e.g., playful, fun, adventurous"
                  className={`input text-sm ${criteria.mood ? 'bg-green-50 border-green-200' : ''}`}
                />
              </div>

              {/* Audience */}
              <div>
                <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                  <span>Target Audience {criteria.audience && '✓'}</span>
                  <button
                    onClick={() => toggleLock('audience')}
                    className="text-xs"
                    title={criteriaLocked.audience ? 'Unlock' : 'Lock'}
                  >
                    {criteriaLocked.audience ? '🔒' : '🔓'}
                  </button>
                </label>
                <input
                  type="text"
                  value={criteria.audience}
                  onChange={(e) => handleCriteriaChange('audience', e.target.value)}
                  placeholder="e.g., kids, adults, teens"
                  className={`input text-sm ${criteria.audience ? 'bg-green-50 border-green-200' : ''}`}
                />
              </div>

              {/* Use Case */}
              <div>
                <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                  <span>Use Case {criteria.useCase && '✓'}</span>
                  <button
                    onClick={() => toggleLock('useCase')}
                    className="text-xs"
                    title={criteriaLocked.useCase ? 'Unlock' : 'Lock'}
                  >
                    {criteriaLocked.useCase ? '🔒' : '🔓'}
                  </button>
                </label>
                <input
                  type="text"
                  value={criteria.useCase}
                  onChange={(e) => handleCriteriaChange('useCase', e.target.value)}
                  placeholder="e.g., laptop, water bottle"
                  className={`input text-sm ${criteria.useCase ? 'bg-green-50 border-green-200' : ''}`}
                />
              </div>

              {/* Size */}
              <div>
                <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                  <span>Size Preference {criteria.size && '✓'}</span>
                  <button
                    onClick={() => toggleLock('size')}
                    className="text-xs"
                    title={criteriaLocked.size ? 'Unlock' : 'Lock'}
                  >
                    {criteriaLocked.size ? '🔒' : '🔓'}
                  </button>
                </label>
                <input
                  type="text"
                  value={criteria.size}
                  onChange={(e) => handleCriteriaChange('size', e.target.value)}
                  placeholder="e.g., small, 2 inches"
                  className={`input text-sm ${criteria.size ? 'bg-green-50 border-green-200' : ''}`}
                />
              </div>

              {/* Quantity */}
              <div>
                <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                  <span>Quantity {criteria.quantity && '✓'}</span>
                  <button
                    onClick={() => toggleLock('quantity')}
                    className="text-xs"
                    title={criteriaLocked.quantity ? 'Unlock' : 'Lock'}
                  >
                    {criteriaLocked.quantity ? '🔒' : '🔓'}
                  </button>
                </label>
                <input
                  type="text"
                  value={criteria.quantity}
                  onChange={(e) => handleCriteriaChange('quantity', e.target.value)}
                  placeholder="e.g., 12 stickers, dozen"
                  className={`input text-sm ${criteria.quantity ? 'bg-green-50 border-green-200' : ''}`}
                />
              </div>
            </div>

            <div className="mt-4 p-3 bg-gray-50 rounded text-xs text-gray-600">
              <p className="mb-1">
                <strong>How it works:</strong>
              </p>
              <ul className="space-y-1">
                <li>• Chat fills fields automatically (🔓 unlocked)</li>
                <li>• Edit a field manually to lock it (🔒 locked)</li>
                <li>• Locked fields won't be overwritten by chat</li>
                <li>• Click 🔒/🔓 to toggle lock state</li>
                <li>• Green background = field has a value</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Planning Level Slider */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              🎯 Planning Level
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between text-xs text-gray-600">
                <span>Quick</span>
                <span>Balanced</span>
                <span>Detailed</span>
              </div>
              <input
                type="range"
                min="0"
                max="2"
                step="1"
                value={planningLevel === 'automatic' ? 0 : planningLevel === 'moderate' ? 1 : 2}
                onChange={(e) => {
                  const level = ['automatic', 'moderate', 'detailed'][parseInt(e.target.value)];
                  setPlanningLevel(level);
                }}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                disabled={messages.length > 0}
              />
              <div className="text-xs text-gray-700 bg-gray-50 p-3 rounded">
                {planningLevel === 'automatic' && (
                  <>
                    <strong>Automatic:</strong> One prompt, AI decides everything. Fast and creative.
                  </>
                )}
                {planningLevel === 'moderate' && (
                  <>
                    <strong>Moderate:</strong> A few questions to refine your design. Balanced approach.
                  </>
                )}
                {planningLevel === 'detailed' && (
                  <>
                    <strong>Detailed:</strong> Comprehensive questions for a perfect custom design.
                  </>
                )}
              </div>
              {messages.length > 0 && (
                <p className="text-xs text-amber-600">
                  Planning level locked after conversation starts
                </p>
              )}
            </div>
          </div>

          {/* Project Name/Theme with Help */}
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">
                🎨 Project Name
              </h3>
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="text-indigo-600 hover:text-indigo-700 text-sm"
                title="Help"
              >
                ❓
              </button>
            </div>

            {showHelp && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3 text-xs text-blue-900">
                <p className="font-semibold mb-1">How to use Project Name:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Give your project a memorable name or theme</li>
                  <li>Examples: "Cute Cats", "Retro Space Adventure", "Kawaii Food"</li>
                  <li>This helps you find the project later</li>
                  <li>You can always edit it before creating</li>
                </ul>
              </div>
            )}

            <textarea
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="Enter a name or short description (e.g., 'Cute Dinosaurs' or 'Minimalist Tech Icons')"
              className="input min-h-[100px]"
              rows="4"
            />
            <p className="text-xs text-gray-500 mt-2">
              This will be your project's name and main theme
            </p>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              ⚡ Actions
            </h3>
            <div className="space-y-2">
              {/* Start Generation Button - Primary action */}
              <button
                onClick={createProject}
                disabled={!canStartGeneration || generating}
                className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all ${
                  canStartGeneration && !generating
                    ? 'bg-indigo-600 hover:bg-indigo-700 shadow-md hover:shadow-lg'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
                title={!canStartGeneration ? `Need project name + at least 3 criteria (${criteriaCount}/10 filled)` : 'Start generating stickers'}
              >
                {generating ? '🎨 Generating...' : '✨ Start Generation'}
              </button>

              {!canStartGeneration && (
                <p className="text-xs text-amber-600 text-center">
                  Fill project name + {Math.max(0, 3 - criteriaCount)} more criteria to start
                </p>
              )}

              {/* Start Over */}
              <button
                onClick={() => {
                  if (confirm('Start over? This will clear your conversation and criteria.')) {
                    setMessages([]);
                    setTheme('');
                    setInput('');
                    setCriteria({
                      style: '',
                      artStyle: '',
                      subjects: '',
                      colors: '',
                      borders: '',
                      mood: '',
                      audience: '',
                      useCase: '',
                      size: '',
                      quantity: ''
                    });
                    setCriteriaLocked({
                      style: false,
                      artStyle: false,
                      subjects: false,
                      colors: false,
                      borders: false,
                      mood: false,
                      audience: false,
                      useCase: false,
                      size: false,
                      quantity: false
                    });
                  }
                }}
                className="btn-secondary w-full"
                disabled={generating}
              >
                🔄 Start Over
              </button>
            </div>
          </div>

          {/* Tips */}
          <div className="card bg-indigo-50 border border-indigo-100">
            <h3 className="text-sm font-semibold text-indigo-900 mb-2">
              💡 Tips
            </h3>
            <ul className="text-xs text-indigo-800 space-y-1">
              <li>• Chat fills criteria automatically as you talk</li>
              <li>• Fill fields directly to skip conversation</li>
              <li>• Edited fields lock automatically (🔒)</li>
              <li>• Need 3+ criteria to start generation</li>
              <li>• AI won't ask about filled criteria</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Image Generation Overlay */}
      {generating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
              🎨 Generating Your Stickers
            </h3>

            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-indigo-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${generationProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 text-center mt-2">
                {generationProgress}% complete
              </p>
            </div>

            <div className="space-y-2 text-sm text-gray-700">
              <p className="flex items-center">
                <span className="mr-2">{generationProgress >= 10 ? '✅' : '⏳'}</span>
                Creating project...
              </p>
              <p className="flex items-center">
                <span className="mr-2">{generationProgress >= 50 ? '✅' : '⏳'}</span>
                Generating AI prompts...
              </p>
              <p className="flex items-center">
                <span className="mr-2">{generationProgress >= 100 ? '✅' : '⏳'}</span>
                Creating sticker images...
              </p>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center">
              This may take a few minutes. Please don't close this window.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
